import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getMessage } from '@tcc/i18n';
import { Nullable } from '@tcc/common';
import ApplicationsDetails from '../../layers/inSeason/ApplicationsDetails';
import PreviewCard from '../../../components/cards/cardAndPreview/PreviewCard';
import { trackEvent } from '../../../actions/analyticsActions';
import { useAppSelector } from '../../../utils';
import { getFeatureValue } from '../../../selectors';
import { getCurrentSeason } from '../../../selectors/seasonSelectors';
import { userSelector } from '../../../selectors/userSelectors';
import { getApplicationTreatmentsBySeason } from '../../../selectors/applicationTreatmentSelectors';
import { treatmentsToApplicationsData } from '../../../utils/applicationTreatmentTransformers';
import { FeatureFlagKey } from '../../../types';
import { ApplicationsData } from '../../../types/Application';
import styles from '../../../components/cards/cardAndPreview/cardAndPreview.css';

interface ApplicationCardContainerProps {
  index?: number;
  isPreview?: boolean;
}

const ApplicationCardContainer: React.FC<ApplicationCardContainerProps> = ({ 
  index,
  isPreview = false 
}) => {
  const dispatch = useDispatch();
  const state = useAppSelector(state => state);
  const [expandedApplication, setExpandedApplication] = useState<Nullable<string>>(null);

  const isFeatureEnabled = getFeatureValue(state, { key: FeatureFlagKey.DMB_APPLICATION_CARD });
  const currentSeason = isPreview || typeof index !== 'number' ? '' : getCurrentSeason(state, { index });
  const locale = state.app.locale ?? 'en-us';
  
  const user = userSelector(state);
  const fieldUUID = state.field?.uuid?.toUpperCase() ?? '';

  const treatments = useMemo(
    () => (currentSeason ? getApplicationTreatmentsBySeason(state, currentSeason) : []),
    [state, currentSeason]
  );

  const applicationsData: ApplicationsData = useMemo(() => (
    treatmentsToApplicationsData(treatments, {
      userUuid: user?.uuid ?? '',
      fieldUUID,
    })
  ), [treatments, user?.uuid, fieldUUID]);

  if (!isFeatureEnabled) {
    return null;
  }

  const handleToggleApplicationExpand = (applicationKey: Nullable<string>) => {
    if(applicationKey !== expandedApplication) {
      setExpandedApplication(applicationKey);
      dispatch(trackEvent('DMB - Individual Application Expanded', {applicationKey}));
    } else {
      setExpandedApplication(null);
    }
  };

  if (isPreview) {
    return (
      <PreviewCard testId="application-card-preview">
        <div className={styles.spacedRow}>
          <div className={styles.column}>
            <div className={styles.value}>{applicationsData.applications.length}</div>
            <h3>{getMessage('application.card.title')}</h3>
          </div>
        </div>
      </PreviewCard>
    );
  }

  return (
    <div className={styles.applicationCardContainer}>
      <ApplicationsDetails
        applicationsData={applicationsData}
        testId="application-card-container"
        expandedApplication={expandedApplication}
        onToggleApplicationExpand={handleToggleApplicationExpand}
        season={currentSeason}
        locale={locale}
      />
    </div>
  );
};

export default ApplicationCardContainer;
